"use client";

// THE POST'S LIVE PIECES. A blog post is markdown; a fenced block written
//
//   ```embed shelf
//   ```
//
// renders one of these instead of code. Each is the real thing made small —
// the community's own card, chips, link bar and search, in the community's own
// room (sage, the skills ground) — never a picture of it. Everything clickable
// works: cards open the real skill, copy copies a real link, the chips sort.

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import BrandIcon from "../../BrandIcon";
import { LinkBar } from "../../skills/PasteUrl";
import { rank, termsOf } from "../../../lib/community/search";
import { BRING_EXAMPLES, SHELF, SITE, type EmbedSkill } from "./data";
import "../../community/community.css";
import "../../community/night.css";
import "./embeds.css";
import ShareEmbed from "./ShareEmbed";
import { nettiSrc } from "./nettiSrc";

export const EMBED_NAMES = ["shelf", "link", "bring", "chat", "share", "rooms"] as const;
export type EmbedName = (typeof EMBED_NAMES)[number];

// ── netti, the real render ──────────────────────────────────────────────────
// Never a css orb: the house creature at the room's exact hue, painted for the
// ground it lands on (paper on a pale room, lit glass on a dark one), from
// /api/brand/netti?hue=…&ground=… (lib/brand/groundTrue).

export const ROOM_LOOK = {
  skills: { hue: 172, ground: "hsl(193 58% 10%)" },
  guides: { hue: 96, ground: "hsl(37 52% 86%)" },
  resources: { hue: 281, ground: "hsl(222 52% 86%)" },
  share: { hue: 217, ground: "#fbf6ee" },
} as const;

function Netti({ room, className }: { room: keyof typeof ROOM_LOOK; className: string }) {
  const r = ROOM_LOOK[room];
  // eslint-disable-next-line @next/next/no-img-element
  return <img className={className} src={nettiSrc(r.hue, r.ground)} alt="" aria-hidden loading="lazy" decoding="async" />;
}

// ── small marks ─────────────────────────────────────────────────────────────

function SortArrow({ up }: { up: boolean }) {
  return (
    <svg className="cm-sort__arrow" data-up={up || undefined} width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M6 2.2v7.4M2.9 6.6 6 9.7l3.1-3.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Star() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
      <path d="M6 .9l1.5 3.3 3.6.4-2.7 2.4.8 3.5L6 8.7l-3.2 1.8.8-3.5L.9 4.6l3.6-.4L6 .9z" />
    </svg>
  );
}
function CopyMark({ done }: { done: boolean }) {
  return done ? (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M3 7.3 5.8 10 11 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <rect x="8" y="8" width="12" height="12" rx="2.5" />
      <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
    </svg>
  );
}

function useCopy() {
  const [done, setDone] = useState<string | null>(null);
  const copy = async (key: string, text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { return; }
    setDone(key);
    window.setTimeout(() => setDone((d) => (d === key ? null : d)), 1500);
  };
  return { done, copy };
}

/** the stage: a piece of the community's room, set into the page. */
function Stage({ room = "skills", caption, children, wide = false }: { room?: "skills" | "guides" | "resources" | "share"; caption?: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <figure className={`bx${wide ? " bx--wide" : ""}${room === "skills" ? " cm-night" : ""}`} data-room={room}>
      <div className="bx-stage">
        <Netti room={room} className="bx-stage__netti" />
        <div className="bx-stage__in">{children}</div>
      </div>
      {caption && <figcaption className="bx-cap">{caption}</figcaption>}
    </figure>
  );
}

function SkillCard({ s, copied, onCopy, out }: { s: EmbedSkill; copied: boolean; onCopy: () => void; out?: boolean }) {
  return (
    <article className="cm-card bx-card" data-out={out ? "" : undefined}>
      <span className="cm-card__top">
        <span className="cm-card__cat">{s.category}</span>
        {s.version && <span className="cm-card__ver">v{s.version}</span>}
        <button type="button" className="cm-card__copy" data-done={copied || undefined} onClick={onCopy} aria-label={copied ? "link copied" : `copy the link to ${s.title}`}>
          <CopyMark done={copied} />
        </button>
      </span>
      <Link className="cm-card__t cm-card__link" href={`/skills/${s.slug}`}>{s.title}</Link>
      <span className="cm-card__d">{s.description}</span>
      <span className="cm-card__tags">{s.tags.slice(0, 3).map((t) => <span key={t} className="cm-tag">{t}</span>)}</span>
      <span className="cm-card__by">
        <BrandIcon name={s.by === "innernet" ? "innernet" : "github"} size={16} />
        <span className="cm-card__keeper">
          {s.broughtBy || s.host ? "by" : "kept by"} <b>{s.by}</b>
          {s.host && <span className="cm-card__author"> · {s.host}</span>}
          {s.broughtBy && <span className="cm-card__author"> · brought by {s.broughtBy}</span>}
        </span>
      </span>
    </article>
  );
}

// ── shelf: the room itself — sort chip, top, search, copy ───────────────────

type Sort = "newest" | "oldest" | "top";

function ShelfEmbed() {
  const [sort, setSort] = useState<Sort>("newest");
  const [q, setQ] = useState("");
  const { done, copy } = useCopy();
  const searching = termsOf(q).length > 0;
  const hits = useMemo(() => new Map(rank(SHELF, q, (s) => ({ title: s.title, description: s.description, tags: s.tags, category: s.category, author: s.by })).map((h) => [h.item.slug, h.score])), [q]);
  // `top` in the post is illustrative ONLY in the order it shows: the shelf
  // counted its first uses on 2026-09-18, so the post says so under the stage.
  const TOP = ["superpowers-systematic-debugging", "quiet-interface", "anthropic-frontend-design", "supabase-postgres-best-practices"];
  const list = useMemo(() => {
    const l = [...SHELF];
    if (searching) return l.sort((a, b) => (hits.get(b.slug) ?? 0) - (hits.get(a.slug) ?? 0));
    if (sort === "top") return l.sort((a, b) => ((TOP.indexOf(a.slug) + 99) % 99) - ((TOP.indexOf(b.slug) + 99) % 99));
    return l.sort((a, b) => (sort === "newest" ? b.updated.localeCompare(a.updated) : a.updated.localeCompare(b.updated)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, searching, hits]);
  const shown = searching ? list.filter((s) => hits.has(s.slug)) : list;
  return (
    <Stage caption="the shelf, small. the chips sort, the search filters as you type, the discs copy a real link, and every card opens its real page.">
      <div className="bx-bar">
        <span className="bx-crumb"><BrandIcon name="innernet" size={15} /> community <span className="bx-slash">/</span>
          <input className="input-soft bx-search" value={q} onChange={(e) => setQ(e.target.value.slice(0, 40))} placeholder="search" aria-label="search the shelf" />
        </span>
        <span className="bx-count">{shown.length} skills</span>
      </div>
      <div className="cm-lenses bx-chips">
        <button type="button" className="chip cm-sort" data-on={sort !== "top" ? "true" : undefined} onClick={() => setSort(sort === "newest" ? "oldest" : "newest")}>
          {sort === "oldest" ? "oldest" : "newest"}<SortArrow up={sort === "oldest"} />
        </button>
        <button type="button" className="chip cm-sort" data-on={sort === "top" ? "true" : undefined} aria-pressed={sort === "top"} onClick={() => setSort(sort === "top" ? "newest" : "top")}>
          <Star />top
        </button>
        <span className="cm-lenses__sep" aria-hidden />
        {["design", "code", "writing", "documents"].map((c) => (
          <button key={c} type="button" className="chip" data-on={q === c ? "true" : undefined} onClick={() => setQ(q === c ? "" : c)}>{c}</button>
        ))}
      </div>
      <div className="cm-grid bx-grid" data-searching={searching || undefined}>
        {(searching ? list : shown).slice(0, 9).map((s) => (
          <SkillCard key={s.slug} s={s} copied={done === s.slug} onCopy={() => copy(s.slug, `${SITE}/skills/${s.slug}`)} out={searching && !hits.has(s.slug)} />
        ))}
      </div>
      {searching && shown.length === 0 && <p className="bx-empty">nothing here matches &ldquo;{q}&rdquo;. on the real page, it would walk you to the room that has it.</p>}
    </Stage>
  );
}

// ── link: the skill page's link bar ─────────────────────────────────────────

function LinkEmbed() {
  const url = `${SITE}/skills/superpowers-systematic-debugging`;
  const [done, setDone] = useState(false);
  return (
    <Stage caption="a skill's link, whole, on one line. when it is longer than its field it drifts past like a ticker; hover and it holds still.">
      <div className="bx-skillhead">
        <span className="bx-eyebrow">a skill, kept by obra</span>
        <span className="bx-title">systematic debugging</span>
      </div>
      <div className="sk-paste">
        <p className="sk-paste-say">paste this link into your ai. it will know what to do.</p>
        <LinkBar url={url} done={done} onCopy={async () => { try { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* */ } }} />
      </div>
      <div className="detail-rows sk-ledger bx-ledger">
        {[["kind", "skill"], ["when to load it", "the moment something behaves unexpectedly."], ["kept by", "obra"], ["source", "github.com/obra/superpowers"], ["reuse", "MIT"]].map(([a, b]) => (
          <div key={a} className="detail-row"><span>{a}</span><span>{b}</span></div>
        ))}
      </div>
    </Stage>
  );
}

// ── bring: any way a skill is shared, read as the repo it means ─────────────

function BringEmbed() {
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState<"said" | "reading" | "landed">("landed");
  const ex = BRING_EXAMPLES[i];
  const pick = (n: number) => {
    setI(n);
    setPhase("reading");
    window.setTimeout(() => setPhase("landed"), 650);
  };
  return (
    <Stage caption="hand your ai whatever you were handed. innernet reads it as the repo it points at, and the skill lands under its author's name.">
      <div className="bx-tabs" role="tablist">
        {BRING_EXAMPLES.map((e, n) => (
          <button key={e.kind} role="tab" aria-selected={n === i} type="button" className="chip" data-on={n === i ? "true" : undefined} onClick={() => pick(n)}>{e.kind}</button>
        ))}
      </div>
      <div className="bx-bring">
        <div className="bx-said"><span className="bx-said__who">you paste</span><code>{ex.said}</code></div>
        <div className="bx-thread" data-phase={phase} aria-hidden><span /></div>
        <div className="bx-lands" data-phase={phase}>
          <span className="bx-lands__where">{ex.lands}</span>
          <span className="bx-lands__t">{ex.title}</span>
          <span className="bx-lands__by">by <b>{ex.by}</b> · brought by <b>you</b></span>
          <span className="bx-lands__note">{ex.note}</span>
        </div>
      </div>
    </Stage>
  );
}

// ── chat: finding and keeping a skill without leaving the conversation ──────

const CHAT: { who: "you" | "ai" | "tool"; text: string }[] = [
  { who: "you", text: "my tests started failing after the migration and i can't see why." },
  { who: "tool", text: "innernet_find_skills · “failing tests after a migration”" },
  { who: "ai", text: "there's a skill on the innernet community for exactly this: systematic debugging, by obra. reproduce, isolate, understand, and only then propose a fix. want me to work through it with you, or keep it in your innernet for next time?" },
  { who: "you", text: "both." },
  { who: "tool", text: "innernet_find_skills · { name: “superpowers-systematic-debugging” }" },
  { who: "tool", text: "innernet_add_skill · into your “skills” project" },
  { who: "ai", text: "kept. it's a private page in your innernet now, so claude, chatgpt and cursor can all load it. first step: let's reproduce the failure on its own. which test fails first?" },
];

function ChatEmbed() {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setN(CHAT.length); return; }
    let t = 0;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const step = (k: number) => { setN(k); if (k < CHAT.length) t = window.setTimeout(() => step(k + 1), CHAT[k]?.who === "tool" ? 700 : 1300); };
      step(1);
    }, { threshold: 0.35 });
    io.observe(el);
    return () => { io.disconnect(); window.clearTimeout(t); };
  }, []);
  return (
    <Stage room="share" caption="inside any chat you have connected innernet to. the tools are real and live today; the words are an example.">
      <div ref={ref} className="bx-chat" aria-live="polite">
        {CHAT.slice(0, Math.max(n, 1)).map((m, k) => (
          <div key={k} className={`bx-msg bx-msg--${m.who}`}>
            {m.who === "tool" ? <><BrandIcon name="innernet" size={13} /><span>{m.text}</span></> : m.text}
          </div>
        ))}
        {n >= CHAT.length && <button type="button" className="quiet-link bx-replay" onClick={() => setN(1)}>play it again</button>}
      </div>
    </Stage>
  );
}

// ── rooms: skills, guides, resources ───────────────────────────────────────

function RoomsEmbed() {
  const rooms = [
    { key: "skills", href: "/community", line: "things an ai can learn from a link, kept by innernet, by members, and by people around the web." },
    { key: "guides", href: "/community/guides", line: "short, honest walkthroughs for the parts of innernet worth learning once." },
    { key: "resources", href: "/community/resources", line: "the tools, clients and reading that go well with a memory of your own." },
  ] as const;
  return (
    <div className="bx-rooms">
      {rooms.map((r) => (
        <Link key={r.key} href={r.href} className="bx-room" data-room={r.key}>
          <Netti room={r.key} className="bx-room__netti" />
          <span className="bx-room__name">{r.key}</span>
          <span className="bx-room__line">{r.line}</span>
          <span className="bx-room__go">open it &rarr;</span>
        </Link>
      ))}
    </div>
  );
}

// ── share: the door you open on a page of your own ─────────────────────────


export default function Embed({ name }: { name: string }) {
  switch (name as EmbedName) {
    case "shelf": return <ShelfEmbed />;
    case "link": return <LinkEmbed />;
    case "bring": return <BringEmbed />;
    case "chat": return <ChatEmbed />;
    case "rooms": return <RoomsEmbed />;
    case "share": return <ShareEmbed />;
    default: return null;
  }
}
